import {
  Grid,
  TextField,
  FormControlLabel,
  Box,
  FormControl,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Field, FormikProps, useFormikContext } from "formik";
import { isNil } from "ramda";
import React, { memo } from "react";
import { Data } from "../../../store/merchant/types";
import SectionContainer from "../../../common/SectionContainer";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import CustomizedSwitch from "../../../common/formElements/Switch";
import moment from "moment-timezone";
import OptionalToolTip from "../../../common/toolTip/OptionalToolTip";
import { get } from "lodash";
import { connect } from "react-redux";
import { ConfigPropExtenderHoc } from "../../../common/StaticConfigProvider";
import TextInput from "../../../common/formElements/TextInput";
import {InputAdornment} from "@mui/material";

interface EditMerchantInformationComponent {
  merchant?: Data;
  toolTips?: { merchants?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
}

const stateToTimezoneMap = {
  NSW: "Australia/Sydney",
  QLD: "Australia/Brisbane",
  VIC: "Australia/Melbourne",
  TAS: "Australia/Hobart",
  NT: "Australia/Darwin",
  ACT: "Australia/Sydney",
  SA: "Australia/Adelaide",
  WA: "Australia/Perth",
};

const LIST_TIME_ZONE = moment.tz.names();
const LIST_TIME_ZONE_AU = LIST_TIME_ZONE.filter((i) =>
  i.includes("Australia/")
);

const MerchantConfigurationForm = (props: EditMerchantInformationComponent) => {
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;
  const { isUpdateAllowed } = props;

  const onChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("scheduleJob.enabled", event.target.checked);
    if (event.target.checked && !values?.scheduleJob) {
      setFieldValue("scheduleJob.timezone", "Australia/Sydney");
    }
  };

  return (
    <Grid item xs={12}>
      <SectionContainer name="sections.Settlements" heading="Settlements">
        <SectionWrapper isChild={true} title="Option">
          <Grid container xs={12} spacing={2}>
            <Grid item xs={12} md={3}>
              <Box p={2} pt={0}>
                <OptionalToolTip
                  tooltip={get(
                    props,
                    `toolTips.merchants.options.configuration.AutoSettlement`
                  )}
                >
                  <FormControlLabel
                    control={
                      <CustomizedSwitch
                        checked={Boolean(values?.scheduleJob?.enabled)}
                        onChange={onChangeCheckbox}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Auto Settlement"
                  />
                </OptionalToolTip>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <OptionalToolTip
                tooltip={get(
                  props,
                  `toolTips.merchants.options.configuration.AutoSettlementTime`
                )}
              >
                <Box>
                  <FormControl fullWidth variant="outlined">
                    <Field
                      name="scheduleJob.timeSchedule"
                      as={TextField}
                      type="time"
                      fullWidth
                      variant="outlined"
                      disabled={
                        !Boolean(values?.scheduleJob?.enabled) ||
                        !isUpdateAllowed
                      }
                      label="Auto-Settle Time"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      style={
                        !values?.scheduleJob?.enabled
                          ? { pointerEvents: "none" }
                          : {}
                      }
                    />
                  </FormControl>
                  <Box mt={1}>
                    <Typography>
                      <strong>Last Execution: </strong>
                      {values?.scheduleJob?.lastExecution &&
                        moment(values?.scheduleJob?.lastExecution).format(
                          "DD/MM/YYYY HH:MM:SS"
                        )}
                    </Typography>
                  </Box>
                </Box>
              </OptionalToolTip>
            </Grid>
            <Grid item xs={12} md={4}>
              <OptionalToolTip
                tooltip={get(
                  props,
                  `toolTips.merchants.options.configuration.AutoSettlementTimezone`
                )}
              >
                <Autocomplete
                  value={stateToTimezoneMap[values?.attributes?.State]}
                  disabled={
                    true
                  }
                  id="schedule-timezone-autocomplete"
                  options={LIST_TIME_ZONE_AU}
                  defaultValue=""
                  onChange={(_event: React.ChangeEvent<{}>, value) => {
                    !isNil(value) &&
                      setFieldValue("scheduleJob.timezone", value);
                  }}
                  disableClearable={true}
                  renderInput={(params) => (
                    <TextField
                      variant="outlined"
                      {...params}
                      label="Time Zone"
                    />
                  )}
                />
              </OptionalToolTip>
              <Box mt={1}>
                <Typography>
                  <strong>Last Completed: </strong>
                  {values?.scheduleJob?.lastCompleted &&
                    moment(values?.scheduleJob?.lastCompleted).format(
                      "DD/MM/YYYY HH:MM:SS"
                    )}
                </Typography>
              </Box>
            </Grid>
              <Grid item xs={12} md={3}>
              <Box p={2} pt={0}>
                <OptionalToolTip
                  tooltip={get(
                    props,
                    `toolTips.merchants.options.configuration.CashOutDailyLimitEnabled`
                  )}
                >
                  <FormControlLabel
                    control={
                      <CustomizedSwitch
                        checked={Boolean(values.attributes.CashOutDailyLimitEnabled)}
                        onChange={(event, value) => {
                          setFieldValue(
                            "attributes.CashOutDailyLimitEnabled",
                            value
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Cash Out Daily Limit Enabled"
                  />
                </OptionalToolTip>
              </Box>
            </Grid>
          <Grid item xs={12} md={3}>
            <TextInput
                key={`attributes.CashOutDailyLimit`}
                type="string"
                fieldname="attributes.CashOutDailyLimit"
                label="Cash Out Daily Limit"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </SectionWrapper>
      </SectionContainer>
    </Grid>
  );
};

const ConnectedMerchantConfigurationForm = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: EditMerchantInformationComponent) => {
    return {
      ...ownProps,
    };
  }
)(MerchantConfigurationForm);

const ConnectedMerchantConfigurationFormHoc = (
  props: EditMerchantInformationComponent
) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedMerchantConfigurationForm {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedMerchantConfigurationFormHoc;
